import { useId, useState } from 'react'
import { type Series, exactNumber, seriesColor } from './scale'

/**
 * The frame every chart in this folder is drawn in.
 *
 * It owns the four things that are the same for all of them and are each easy to forget once per
 * chart: the SVG scales instead of overflowing, two or more series always have a legend, the numbers
 * exist as a table for anyone not reading the picture, and the hover layer is a real tooltip rather
 * than a native title attribute.
 *
 * The table is not a fallback nobody sees. It is the same figures the marks encode, available to a
 * screen reader and — through the toggle — to anyone who would rather read the values than estimate
 * them off an axis. A chart whose data can only be recovered by measuring pixels is not finished.
 */
export interface ChartFrameProps {
  title: string
  /** One sentence saying what the picture shows, for a reader who cannot see it. */
  description: string
  categories: string[]
  series: Series[]
  /** The unit for the table's value column, if the series are not counts of the same thing. */
  categoryHeading?: string
  /** Set when a single series makes a legend redundant: the title already names what is plotted. */
  hideLegend?: boolean
  children: React.ReactNode
}

export function ChartFrame({
  title,
  description,
  categories,
  series,
  categoryHeading = 'Day',
  hideLegend,
  children
}: ChartFrameProps) {
  const [showTable, setShowTable] = useState(false)
  const titleId = useId()
  const descriptionId = useId()
  const tableId = useId()

  // One series carries its identity in the title; a swatch box beside it would only restate it.
  const legend = !hideLegend && series.length > 1

  return (
    <figure className="chart">
      <figcaption className="chart-head">
        <span className="chart-title" id={titleId}>
          {title}
        </span>
        <button
          type="button"
          className="chart-table-toggle"
          aria-expanded={showTable}
          aria-controls={tableId}
          onClick={() => setShowTable((open) => !open)}
        >
          {showTable ? 'Hide values' : 'Show values'}
        </button>
      </figcaption>

      {legend && (
        <ul className="chart-legend">
          {series.map((entry, index) => (
            <li key={entry.key}>
              <span className="chart-swatch" style={{ background: seriesColor(index) }} aria-hidden="true" />
              {entry.label}
            </li>
          ))}
        </ul>
      )}

      <div className="chart-plot" role="img" aria-labeledby={`${titleId} ${descriptionId}`}>
        {children}
      </div>

      <p className="chart-description" id={descriptionId}>
        {description}
      </p>

      <div className="table-scroll" id={tableId} hidden={!showTable}>
        <table className="chart-table">
          <thead>
            <tr>
              <th>{categoryHeading}</th>
              {series.map((entry) => (
                <th key={entry.key}>{entry.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((category, row) => (
              <tr key={category}>
                <td>{category}</td>
                {series.map((entry) => (
                  <td key={entry.key}>{exactNumber(entry.values[row] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  )
}

export interface TooltipRow {
  label: string
  value: number
  color: string
}

export interface TooltipState {
  /** Fractions of the plot's width and height, so the tooltip follows the SVG as it scales. */
  x: number
  y: number
  heading: string
  rows: TooltipRow[]
}

/**
 * The hover readout.
 *
 * Positioned in percentages rather than pixels because the SVG is scaled by the browser to whatever
 * width the card gives it, and a tooltip placed at an SVG coordinate would drift away from the mark
 * it belongs to at every width but one.
 *
 * It flips to the left of the pointer past the halfway mark so it never leaves the card, which is the
 * failure the layout gate would catch as a page scrolling sideways.
 */
export function ChartTooltip({ state }: { state: TooltipState | null }) {
  if (!state) return null

  return (
    <div
      className="chart-tooltip"
      role="status"
      style={{
        left: `${state.x * 100}%`,
        top: `${state.y * 100}%`,
        transform: `translate(${state.x > 0.5 ? 'calc(-100% - 12px)' : '12px'}, -50%)`
      }}
    >
      <strong>{state.heading}</strong>
      {state.rows.map((row) => (
        <span key={row.label} className="chart-tooltip-row">
          <span className="chart-swatch" style={{ background: row.color }} aria-hidden="true" />
          {row.label}
          <span className="chart-tooltip-value">{exactNumber(row.value)}</span>
        </span>
      ))}
    </div>
  )
}

/** The gridlines and the value axis, drawn the same way by every chart that has one. */
export function ValueAxis({
  ticks,
  scale,
  left,
  right,
  format
}: {
  ticks: number[]
  scale: (value: number) => number
  left: number
  right: number
  format: (value: number) => string
}) {
  return (
    <g className="chart-axis" aria-hidden="true">
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={left} x2={right} y1={scale(tick)} y2={scale(tick)} className="chart-gridline" />
          <text x={left - 8} y={scale(tick)} dy="0.32em" textAnchor="end" className="chart-tick">
            {format(tick)}
          </text>
        </g>
      ))}
    </g>
  )
}
